import type { Building } from "building-types/building/buildingTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ViewOverridableHandle } from "building-types/selectors/selectorTypes";
import { multimapFlatten } from "common/data/multimap";
import type { RWTable, Table } from "common/data/table";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import {
  generateHighlightHandlesFromDimensions,
  getEntityHighlightHandles,
} from "../generationTools/generateSelectorHighlightRelationsTools";
import memo from "../utils/memo";

function select({
  doc: { selectors },
  exteriorDimensions,
  interiorDimensions,
  foundationExteriorDimensions,
  foundationInteriorDimensions,
}: Pick<
  Building,
  "doc" | "exteriorDimensions" | "interiorDimensions" | "foundationExteriorDimensions" | "foundationInteriorDimensions"
>) {
  return {
    selectors,
    exteriorDimensions,
    foundationExteriorDimensions,
    foundationInteriorDimensions,
    interiorDimensions,
  };
}

/**
 * Generate a table that maps from persisted handles (those found in selectors)
 * to the runtime handle that is used for entity highlighting.
 */
function generate(opts: ReturnType<typeof select>): {
  selectorIdToEntityHighlightHandle: Table<ViewOverridableHandle>;
} {
  const hashToHandle: { [docEntityHandleHash: string]: ViewOverridableHandle } = {};

  for (const levelId in opts.exteriorDimensions) {
    const exteriorDimensions = opts.exteriorDimensions[levelId];
    getEntityHighlightHandles(
      hashToHandle,
      multimapFlatten(exteriorDimensions).map((d) => d.docEntityHandle) as DocEntityHandle[],
    );
  }
  for (const levelId in opts.interiorDimensions) {
    const interiorDimensions = opts.interiorDimensions[levelId];
    getEntityHighlightHandles(
      hashToHandle,
      multimapFlatten(interiorDimensions).map((d) => d.docEntityHandle) as DocEntityHandle[],
    );
  }
  generateHighlightHandlesFromDimensions(opts.foundationExteriorDimensions, hashToHandle);
  generateHighlightHandlesFromDimensions(opts.foundationInteriorDimensions, hashToHandle);

  const selectorIdToEntityHighlightHandle: RWTable<ViewOverridableHandle> = {};
  for (const id in opts.selectors) {
    const selector = opts.selectors[id];
    const highlightHandle = hashToHandle[docEntityHandleHash(selector.entityHandle)];
    if (!highlightHandle) {
      continue;
    }
    selectorIdToEntityHighlightHandle[id] = highlightHandle;
  }

  return {
    selectorIdToEntityHighlightHandle,
  };
}

export default memo(select, generate);
